"use client";

import { Trans, useLingui } from "@lingui/react/macro";
import {
  createContext,
  useContext,
  useState,
  useCallback,
  useRef,
  useEffect,
  type ReactNode,
} from "react";

import { cn } from "@/utils/shadcn-ui";

import type { ConfirmOptions, DialogService } from "./dialog-service";

import { Dialog, DialogContent } from "./base/dialog";
import { Button } from "./button";
import {
  DIALOG_CARD_CLASS,
  DialogBody,
  DialogFooter,
  DialogHeader,
} from "./dialog";

type DialogOptions = {
  title?: string;
  body: ReactNode;
  /**
   * Given a function, it receives the resolver, so a custom footer can settle
   * the promise with `true` rather than only the `false` a dismissal gives.
   */
  footer?: ReactNode | ((close: (value: boolean) => void) => ReactNode);
  className?: string;
};

type DialogContextType = {
  openDialog: (options: DialogOptions) => Promise<boolean>;
  alert: (message: ReactNode, title?: string) => Promise<boolean>;
  confirm: (
    message: string,
    title?: string,
    options?: ConfirmOptions,
  ) => Promise<boolean>;
};

const DialogContext = createContext<DialogContextType | null>(null);

export function DialogProvider({ children }: { children: ReactNode }) {
  const { t } = useLingui();
  const [isOpen, setIsOpen] = useState(false);
  const [config, setConfig] = useState<DialogOptions | null>(null);
  const resolverRef = useRef<((value: boolean) => void) | null>(null);

  const openDialog = useCallback((options: DialogOptions) => {
    return new Promise<boolean>((resolve) => {
      // A dialog opened over another one replaces it, and the caller waiting on
      // the one being replaced never hears from it otherwise. Settling it as a
      // dismissal is what the user sees happen: their choice was never taken.
      resolverRef.current?.(false);
      setConfig(options);
      resolverRef.current = resolve;
      setIsOpen(true);
    });
  }, []);

  const close = useCallback((value: boolean) => {
    setIsOpen(false);
    if (resolverRef.current) {
      resolverRef.current(value);
      resolverRef.current = null;
    }
  }, []);

  const alert = useCallback(
    (message: ReactNode, title = t`Notice`) =>
      openDialog({
        title,
        body: <div className="text-sm text-muted-foreground">{message}</div>,
        footer: (
          <Button variant="primary" onClick={() => close(true)}>
            <Trans>OK</Trans>
          </Button>
        ),
      }),
    [openDialog, close, t],
  );

  const confirm = useCallback(
    (message: string, title = t`Confirm`, options: ConfirmOptions = {}) =>
      openDialog({
        title,
        body: <div className="text-sm text-muted-foreground">{message}</div>,
        footer: (
          <>
            <Button onClick={() => close(false)}>
              {options.cancelLabel ?? t`Cancel`}
            </Button>
            <Button
              variant="primary"
              destructive={options.destructive}
              onClick={() => close(true)}
            >
              {options.confirmLabel ?? t`Confirm`}
            </Button>
          </>
        ),
      }),
    [openDialog, close, t],
  );

  // Set global service on mount, clean up on unmount
  useEffect(() => {
    window.dialogService = { alert, confirm } satisfies DialogService;

    return () => {
      delete window.dialogService;
    };
  }, [alert, confirm]);

  return (
    <DialogContext.Provider value={{ openDialog, alert, confirm }}>
      {children}

      <Dialog open={isOpen} onOpenChange={(open) => !open && close(false)}>
        <DialogContent
          className={cn(DIALOG_CARD_CLASS, config?.className)}
          showCloseButton={false}
        >
          <DialogHeader showClose={false}>
            {config?.title || t`Notification`}
          </DialogHeader>

          <DialogBody>{config?.body}</DialogBody>

          {config?.footer && (
            <DialogFooter>
              {typeof config.footer === "function"
                ? config.footer(close)
                : config.footer}
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>
    </DialogContext.Provider>
  );
}

export const useDialog = () => {
  const context = useContext(DialogContext);
  if (!context) throw new Error("useDialog must be used within DialogProvider");
  return context;
};
